import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import { PureComponent, type ReactElement } from "react";
import WindowedSelect from "react-windowed-select";

import { Callout, CalloutIntent, HStack, InfoToggletip, TriggerButton } from "widgets";
import { moveItemsInArray } from "widgets/utils";

import { type ColumnAnnotation } from "../../graphql.js";
import { SortableItem, SortableList } from "../core/forms/Sortable.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import { type ChartSettingsWizardStep } from "./ChartSettings.js";
import { ConditionSortDialog } from "./ConditionSortDialog.js";
import { ANNOTATION_TYPE } from "@/settings/settings.js";

export interface IConditionsSelectorProps {
  label: string; // the title text at the top (e.g. "x-Axis")
  wizardStep: ChartSettingsWizardStep;
  annotations: ColumnAnnotation[]; // name of all available column annotations in the data set
  selectedAnnotations: ColumnAnnotation[]; // names of selected annotations
  onChange?: (selectedAnnotations: ColumnAnnotation[]) => void; // callback called when user added or removed annotations, the callback argument is the current list of selected annotations
  timeSeriesEnabled: boolean;
  limit?: number;
  limitReachedMessage?: string;
  addOptionMessage: string;
  tooltip?: string;
  hideConditionSortDialogButton?: boolean;
  selectClasses?: string;
  formControlClasses?: string;
  noCallout?: boolean; // Just because this component is also used outside the Analytical Data Viewer
}

interface IConditionSelectorState {
  showConditionSortDialog?: boolean;
  sortedCondition?: ColumnAnnotation;
}

interface IColumnAnnotationSelectionOption {
  label: string;
  value: ColumnAnnotation;
}

function getAnnotationTypeLabel(condition: ColumnAnnotation) {
  let typeLabel = "";
  if (condition.type === ANNOTATION_TYPE.NUMERICAL || condition.type === ANNOTATION_TYPE.DURATION) {
    typeLabel = "(numeric)";
  }
  return typeLabel;
}

class ConditionsSelector extends PureComponent<IConditionsSelectorProps, IConditionSelectorState> {
  // private wizardTooltipCallback = null; // The call back feeds the tooltip on the select when no x-axis is selected

  state: IConditionSelectorState = {
    showConditionSortDialog: false,
    sortedCondition: null,
  };

  openConditionSortDialog = (condition: ColumnAnnotation) => {
    this.setState({ showConditionSortDialog: true, sortedCondition: condition });
  };

  closeConditionSortDialog = (callback: () => any = null) => {
    this.setState({ showConditionSortDialog: false }, callback);
  };

  onConditionSortApply = (condition: ColumnAnnotation) => {
    const selectedAnnotations: ColumnAnnotation[] = this.props.selectedAnnotations.map(
      (selectedAnnotation: ColumnAnnotation) =>
        selectedAnnotation.name !== condition.name ? selectedAnnotation : condition,
    );
    this.props.onChange(selectedAnnotations);

    this.closeConditionSortDialog();
  };

  conditionSelected = (condition: IColumnAnnotationSelectionOption) => {
    const selectionAnnotations: ColumnAnnotation[] = this.props.selectedAnnotations.slice();
    const newAnnotation: ColumnAnnotation = {
      ...this.props.annotations.find((a: ColumnAnnotation) => {
        return a.name === condition.value.name;
      }),
    };

    selectionAnnotations.push(newAnnotation);
    this.props.onChange(selectionAnnotations);
  };

  conditionsReordered = ({ oldIndex, newIndex }) => {
    this.props.onChange(arrayMove(this.props.selectedAnnotations, oldIndex, newIndex));
  };

  conditionRemoved = (removedAnnotation: ColumnAnnotation) => {
    const selectionAnnotations: ColumnAnnotation[] = this.props.selectedAnnotations.filter(
      (annotation: ColumnAnnotation) => annotation.name !== removedAnnotation.name,
    );
    this.props.onChange(selectionAnnotations);
  };

  render(): ReactElement {
    const availableAnnotations: ColumnAnnotation[] = this.props.annotations;
    const selectedAnnotations: ColumnAnnotation[] = this.props.selectedAnnotations;

    const annotationOptions: IColumnAnnotationSelectionOption[] =
      availableAnnotations && selectedAnnotations
        ? availableAnnotations
            .filter(
              (annotation: ColumnAnnotation) =>
                !selectedAnnotations.find((selectedAnnotation: ColumnAnnotation) => {
                  return selectedAnnotation.name === annotation.name;
                }),
            )
            .map((annotation: ColumnAnnotation) => {
              return { label: `${annotation.name} ${getAnnotationTypeLabel(annotation)}`, value: annotation };
            })
        : [];

    const selectDisabled: boolean =
      !availableAnnotations ||
      availableAnnotations.length === 0 ||
      (this.props.limit && selectedAnnotations.length >= this.props.limit);
    const selectPlaceholder: string =
      this.props.limit && selectedAnnotations.length >= this.props.limit
        ? this.props.limitReachedMessage
          ? this.props.limitReachedMessage
          : "Limit is reached"
        : this.props.addOptionMessage;
    const selectClassNames = clsx("react-select", `${this.props.selectClasses}`);

    return (
      <div className="chart-conditions-selector selector-component noselect">
        <div className="form-group">
          <span id="data-viewer-x-axis-selection" className="hotspot-beacon" />
          <HStack>
            <label className="control-label">{this.props.label}</label>
            {this.props.tooltip && <InfoToggletip content={this.props.tooltip}></InfoToggletip>}
          </HStack>
          {selectedAnnotations.length > 0 ? (
            <SortableList
              onDragEnd={({ active, over }) => {
                this.props.onChange(
                  moveItemsInArray<ColumnAnnotation, any, any>(this.props.selectedAnnotations, active, over, "field"),
                );
              }}
            >
              <SortableContext items={selectedAnnotations.map((selectedAnnotation) => selectedAnnotation.field)}>
                {selectedAnnotations.map((condition: ColumnAnnotation, index) => (
                  <SortableItem
                    index={index}
                    id={condition.field}
                    key={`item-${index}`}
                    onRemove={() => this.conditionRemoved(condition)}
                    label={condition.name}
                  >
                    <span>
                      {condition.name} {getAnnotationTypeLabel(condition)}
                    </span>
                    {!this.props.hideConditionSortDialogButton && (
                      <TriggerButton
                        label="Configure Conditions"
                        tooltip="Configure how samples are displayed on the x-axis"
                        onPress={() => this.openConditionSortDialog(condition)}
                        isDisabled={
                          this.props.timeSeriesEnabled
                            ? "Configuration is not possible when time series mode is enabled."
                            : null
                        }
                        icon={"settings"}
                      />
                    )}
                  </SortableItem>
                ))}
              </SortableContext>
            </SortableList>
          ) : (
            <>
              {!this.props.noCallout && (
                <Callout intent={CalloutIntent.Info}>
                  <span id="xaxis-select-tip">Select a x-axis condition</span>
                </Callout>
              )}
            </>
          )}
          <div style={{ marginTop: 5 }}>
            <WindowedSelect
              windowThreshold={200}
              className={selectClassNames}
              classNamePrefix={"react-select"}
              name="chartAnnotationSelect"
              placeholder={selectPlaceholder}
              isDisabled={selectDisabled}
              isClearable={false}
              isSearchable={true}
              options={annotationOptions}
              onChange={this.conditionSelected}
              noOptionsMessage={() => "No matching condition..."}
              controlShouldRenderValue={false}
              menuPortalTarget={document.body}
            />
          </div>
          {this.state.showConditionSortDialog && (
            <ModalControlledOverlay close={this.closeConditionSortDialog} isOpen={true}>
              {({ close }) => (
                <ConditionSortDialog
                  condition={this.state.sortedCondition}
                  onCancel={close}
                  onApply={this.onConditionSortApply}
                />
              )}
            </ModalControlledOverlay>
          )}
        </div>
      </div>
    );
  }
}

export { ConditionsSelector };
